(() => {
  'use strict';

  const VIDEO_RE = /(?:youtube\.com|youtu\.be|vimeo\.com|\.mp4(?:$|\?)|\.webm(?:$|\?)|\.mov(?:$|\?)|cdn\.shopify\.com\/videos)/i;
  let modal;
  let frame;
  let lastFocus;

  const isVideoUrl = (url) => Boolean(url && VIDEO_RE.test(url));

  function youtubeId(url) {
    try {
      const parsed = new URL(url, window.location.href);
      if (parsed.hostname.includes('youtu.be')) return parsed.pathname.split('/').filter(Boolean)[0] || '';
      if (parsed.pathname.includes('/embed/')) return parsed.pathname.split('/embed/')[1].split('/')[0];
      if (parsed.pathname.includes('/shorts/')) return parsed.pathname.split('/shorts/')[1].split('/')[0];
      return parsed.searchParams.get('v') || '';
    } catch (_) { return ''; }
  }

  function vimeoId(url) {
    const match = String(url || '').match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return match ? match[1] : '';
  }

  function normaliseEmbed(url, autoplay = true, muted = false, background = false) {
    const yid = youtubeId(url);
    if (yid) {
      const params = new URLSearchParams({ playsinline: '1', controls: background ? '0' : '1', rel: '0', modestbranding: '1' });
      if (autoplay) params.set('autoplay', '1');
      if (muted) params.set('mute', '1');
      if (background) { params.set('loop', '1'); params.set('playlist', yid); }
      return `https://www.youtube.com/embed/${yid}?${params.toString()}`;
    }
    const vid = vimeoId(url);
    if (vid) {
      const params = new URLSearchParams({ playsinline: '1', controls: background ? '0' : '1', title: '0', byline: '0', portrait: '0' });
      if (autoplay) params.set('autoplay', '1');
      if (muted) params.set('muted', '1');
      if (background) { params.set('background', '1'); params.set('loop', '1'); }
      return `https://player.vimeo.com/video/${vid}?${params.toString()}`;
    }
    return url;
  }

  function ensureModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'fs-video-modal';
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Video player');
    modal.innerHTML = `
      <div class="fs-video-modal__backdrop" data-fs-video-close></div>
      <div class="fs-video-modal__dialog">
        <button class="fs-video-modal__close" type="button" data-fs-video-close aria-label="Close video">&times;</button>
        <div class="fs-video-modal__frame"></div>
      </div>`;
    document.body.appendChild(modal);
    frame = modal.querySelector('.fs-video-modal__frame');
    modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-fs-video-close]')) closeModal();
    });
    return modal;
  }

  function mediaFromUrl(url) {
    if (!url) return null;
    const yid = youtubeId(url);
    const vid = vimeoId(url);
    if (yid || vid) {
      const iframe = document.createElement('iframe');
      iframe.src = normaliseEmbed(url, true, false, false);
      iframe.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
      iframe.allowFullscreen = true;
      iframe.title = 'Video player';
      return iframe;
    }
    const video = document.createElement('video');
    video.src = url;
    video.controls = true;
    video.autoplay = true;
    video.playsInline = true;
    video.preload = 'metadata';
    return video;
  }

  function mediaFromVideoMedia(container) {
    const loaded = container.querySelector(':scope > video, :scope > iframe');
    const template = container.querySelector('template');
    let source = loaded ? loaded.cloneNode(true) : null;
    if (!source && template && template.content.firstElementChild) source = template.content.firstElementChild.cloneNode(true);
    if (!source) return null;
    if (source.tagName === 'VIDEO') {
      source.controls = true;
      source.autoplay = true;
      source.muted = false;
      source.loop = false;
      source.playsInline = true;
      source.removeAttribute('poster');
    } else if (source.tagName === 'IFRAME') {
      source.src = normaliseEmbed(source.src, true, false, false);
      source.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
      source.allowFullscreen = true;
    }
    return source;
  }

  function openModal(source, opener) {
    const item = typeof source === 'string' ? mediaFromUrl(source) : source;
    if (!item) return;
    ensureModal();
    lastFocus = opener || document.activeElement;
    frame.replaceChildren(item);
    modal.hidden = false;
    document.body.classList.add('fs-video-modal-open');
    requestAnimationFrame(() => modal.querySelector('.fs-video-modal__close').focus());
    if (item.tagName === 'VIDEO') item.play().catch(() => {});
  }

  function closeModal() {
    if (!modal || modal.hidden) return;
    frame.querySelectorAll('video').forEach((video) => { video.pause(); video.removeAttribute('src'); video.load(); });
    frame.querySelectorAll('iframe').forEach((iframe) => { iframe.src = 'about:blank'; });
    frame.replaceChildren();
    modal.hidden = true;
    document.body.classList.remove('fs-video-modal-open');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  function sourceFromElement(element) {
    const direct = element.closest('[data-fs-video-src]');
    if (direct && direct.dataset.fsVideoSrc) return direct.dataset.fsVideoSrc;
    const link = element.closest('a[href]');
    if (link && isVideoUrl(link.href)) return link.href;
    const video = element.closest('video');
    if (video) return video.currentSrc || video.src || video.querySelector('source')?.src || '';
    const mediaContainer = element.closest('.fs-hero-media, .fs-vgreen-media, .fs-split-media, .fs-kit-video');
    const nestedVideo = mediaContainer?.querySelector('video');
    if (nestedVideo) return nestedVideo.currentSrc || nestedVideo.src || nestedVideo.querySelector('source')?.src || '';
    return '';
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('.fs-video-modal')) return;

    const videoMedia = event.target.closest('video-media');
    if (videoMedia) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openModal(mediaFromVideoMedia(videoMedia), event.target.closest('button,a') || videoMedia);
      return;
    }

    const source = sourceFromElement(event.target);
    if (source && isVideoUrl(source)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openModal(source, event.target.closest('button,a') || event.target);
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeModal();
    if (event.key === 'Tab' && modal && !modal.hidden) {
      const close = modal.querySelector('.fs-video-modal__close');
      event.preventDefault();
      close.focus();
    }
  });

  function addIframeOverlays(root = document) {
    root.querySelectorAll('iframe[src*="youtube"], iframe[src*="youtu.be"], iframe[src*="vimeo"]').forEach((iframe) => {
      if (iframe.closest('.fs-video-modal') || iframe.closest('video-media') || iframe.dataset.fsOverlayReady) return;
      const parent = iframe.parentElement;
      if (!parent) return;
      iframe.dataset.fsOverlayReady = 'true';
      if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fs-video-open-overlay';
      button.dataset.fsVideoSrc = iframe.src;
      button.setAttribute('aria-label', 'Play video larger');
      parent.appendChild(button);
    });
  }

  function setupKitAutoplay(root = document) {
    const items = [...root.querySelectorAll('[data-fs-kit-autoplay]')];
    if (!items.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const card = entry.target;
        const source = card.dataset.fsVideoSrc;
        const holder = card.querySelector('.fs-kit-inline-player');
        if (!holder || !source) return;

        if (!holder.firstElementChild) {
          let media;
          if (youtubeId(source) || vimeoId(source)) {
            media = document.createElement('iframe');
            media.src = normaliseEmbed(source, true, true, true);
            media.allow = 'autoplay; encrypted-media';
            media.title = 'Muted preview video';
          } else {
            media = document.createElement('video');
            media.src = source;
            media.muted = true;
            media.loop = true;
            media.playsInline = true;
            media.preload = 'metadata';
            media.setAttribute('muted', '');
          }
          holder.appendChild(media);
        }

        const media = holder.firstElementChild;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
          if (media.tagName === 'VIDEO') {
            media.play().then(() => card.classList.add('is-fs-playing')).catch(() => {});
          } else {
            card.classList.add('is-fs-playing');
          }
        } else if (media.tagName === 'VIDEO') {
          media.pause();
        }
      });
    }, { threshold: [0, 0.35, 0.7] });
    items.forEach((item) => observer.observe(item));
  }

  function setupScrollAutoplay(root = document) {
    const items = [...root.querySelectorAll('[data-fs-scroll-autoplay]')].filter((item) => !item.dataset.fsScrollAutoplayReady);
    if (!items.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target.querySelector('video');
        if (!video) return;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }, { threshold: [0, 0.35, 0.7] });
    items.forEach((item) => {
      item.dataset.fsScrollAutoplayReady = 'true';
      observer.observe(item);
    });
  }

  function initialise(root = document) {
    root.querySelectorAll('video-media').forEach((videoMedia) => {
      if (videoMedia.querySelector(':scope > .fs-video-open-overlay')) return;
      const overlay = document.createElement('button');
      overlay.type = 'button';
      overlay.className = 'fs-video-open-overlay';
      overlay.setAttribute('aria-label', 'Play video larger');
      videoMedia.appendChild(overlay);
    });
    root.querySelectorAll('a[href]').forEach((link) => {
      if (isVideoUrl(link.href)) link.classList.add('fs-video-popup-trigger');
    });
    addIframeOverlays(root);
    setupKitAutoplay(root);
    setupScrollAutoplay(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
